/* eslint-disable @typescript-eslint/naming-convention */
import { LandingLayout } from 'components/Landing/LandingLayout';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import useMobxStores from 'hooks/useMobxStores';
import React, { useEffect } from 'react';
import { Table, Tr, Td, Tbody } from '@patternfly/react-table';
import { Alert, List, ListItem } from '@patternfly/react-core';
import InfoCircleIcon from '@patternfly/react-icons/dist/esm/icons/info-circle-icon';
import Description from 'components/Course/OverviewTab/Description';
import { Infobox } from '@ole-ui/ole-ui-components';
import useUserTimezone from 'hooks/useUserTimezone';
import { LIVE_CLASS_CANCELLATION_WINDOW } from 'config/constants';
import ClassDaysStepper from './ClassDayStepper';

import './styles.scss';

interface Props {
  description?: string;
  isInaccessible?: boolean;
}

const LiveClassTab = ({ description, isInaccessible }: Props) => {
  const { t } = useTranslation();
  const { formatDatetime } = useUserTimezone();

  const { liveClassStore, routerStore } = useMobxStores();

  const route = routerStore?.route as any;
  const offeringSlug = route?.params?.course;

  useEffect(() => {
    if (offeringSlug) {
      liveClassStore.offeringSlug = offeringSlug;
    }
  }, [route?.params.offeringslug]);

  if (
    liveClassStore.offeringEnrolledLiveClassesLoaded &&
    liveClassStore.offeringEnrolledLiveClass.length === 0
  ) {
    if (isInaccessible) {
      // User can only view schedule
      return (
        <div>
          <Alert
            isInline
            variant="danger"
            title={t('Class cannot be scheduled')}
            className="live-class-tab__alert"
          >
            <div>
              {t(
                'You do not have permissions with your current subscription to schedule this class. Contact support if you have any questions.',
              )}
            </div>
          </Alert>
        </div>
      );
    }

    const attendedInfo = () => {
      if (liveClassStore.isCompletedPerSkuQuotaReached) {
        return t('Attendance limit reached');
      }

      const completedPerSku = liveClassStore.completedPerSkuQuotaUsed;
      if (completedPerSku > 0) {
        return `${t("You've attended")}
        ${liveClassStore.completedPerSkuQuotaUsed}  ${
          completedPerSku === 1 ? t('class') : t('classes')
        }
      `;
      }

      return `${t('You may attend this class up to')}
        ${liveClassStore.completedPerSkuQuotaAllowed}  ${t('times')}.
      `;
    };

    return (
      <>
        <LandingLayout.LiveClassSchedulingInfo>
          <Infobox
            title={t('Class scheduling information')}
            titleIcon={<InfoCircleIcon color="#2B9AF3" />}
            borderColor="#2B9AF3"
          >
            <List>
              <ListItem>
                <Trans>
                  Booking reserves the complete class{' '}
                  <b>from the start date to the end date</b>.
                </Trans>
              </ListItem>

              <ListItem>
                <Trans>
                  Each live class <b>begins at the same time each day</b>.
                </Trans>
              </ListItem>

              {liveClassStore.enrolledQuotaAllowed > 1 && (
                <>
                  <ListItem>
                    <Trans>
                      Your subscription includes{' '}
                      <b>
                        up to two live class reservations per course in total
                      </b>
                      .
                    </Trans>
                  </ListItem>

                  <ListItem>
                    <Trans />
                    You can schedule each course&apos;s live class{' '}
                    <b>twice at most, but not simultaneously</b>.
                  </ListItem>

                  <ListItem>
                    <Trans>
                      To schedule another class after reaching your{' '}
                      <b>two-reservation limit</b>, first cancel an existing
                      reservation.
                    </Trans>
                  </ListItem>
                </>
              )}
              <ListItem>
                <Trans
                  components={{ bold: <b /> }}
                  defaults="Cancellations are accepted <bold>up to {{LIVE_CLASS_CANCELLATION_WINDOW}} days before</bold> the
                class start date."
                  values={{ LIVE_CLASS_CANCELLATION_WINDOW }}
                />
              </ListItem>
              <ListItem>
                {t(
                  'Missed classes without timely cancellation will count toward your maximum allowed reservations.',
                )}
              </ListItem>
            </List>
          </Infobox>
        </LandingLayout.LiveClassSchedulingInfo>

        <LandingLayout.ContentText>{attendedInfo()}</LandingLayout.ContentText>
        <LandingLayout.ContentText>
          {t(
            "No live virtual class scheduled yet. Click the 'Schedule' button to book your class.",
          )}
        </LandingLayout.ContentText>
      </>
    );
  }

  if (
    liveClassStore.offeringEnrolledLiveClassesLoaded &&
    liveClassStore.offeringEnrolledLiveClass.length > 0
  ) {
    const {
      start_time,
      end_time,
      timezone,
      sessions,
      duration_days: durationDays,
      instruction_language: instructionLanguage,
      offering_version: offeringVersion,
    } = liveClassStore?.offeringEnrolledLiveClass?.[0];

    const startTime = formatDatetime(start_time);
    const endTime = formatDatetime(end_time);
    const { isStartingSoon, daysUntilStart, startsSoonWarningDays } =
      liveClassStore;

    return (
      <>
        {daysUntilStart > 0 &&
          (isStartingSoon ? (
            <Alert
              isInline
              variant="info"
              title={t('Class starting soon')}
              className="live-class-tab__alert"
            >
              <div>
                {t(
                  'Your class will start within {{daysUntilStart}} days. You are unable to Reschedule or Cancel this class. Please reach out to Support if you have any questions.',
                  { daysUntilStart },
                )}
              </div>
            </Alert>
          ) : (
            <Alert
              isInline
              variant="info"
              title={t('Manage your class')}
              className="live-class-tab__alert"
            >
              <div>
                {t(
                  'You have until {{dayThreshold}} days before your class start date to Reschedule or Cancel this class.',
                  { dayThreshold: startsSoonWarningDays },
                )}
              </div>
            </Alert>
          ))}
        {description && <Description description={description} />}
        {liveClassStore.offeringEnrolledLiveClassesLoaded &&
          liveClassStore.offeringEnrolledLiveClass && (
            <Table
              isStriped
              borders
              variant="compact"
              aria-label="Live class details"
            >
              <Tbody>
                <Tr>
                  <Td>{t('Starts')}</Td>
                  <Td>
                    {startTime}
                    <div>
                      {t(
                        "Attending a class is available from 15 minutes prior to scheduled start time up until it's marked completed by the instructor.",
                      )}
                    </div>
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('Ends')}</Td>
                  <Td>{endTime}</Td>
                </Tr>
                <Tr>
                  <Td>{t('Duration')}</Td>
                  <Td>
                    {durationDays == null
                      ? '—'
                      : `${durationDays} ${
                          durationDays === 1 ? t('day') : t('days')
                        }`}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('Language')}</Td>
                  <Td>{instructionLanguage}</Td>
                </Tr>
                <Tr>
                  <Td>{t('Version')}</Td>
                  <Td>{offeringVersion}</Td>
                </Tr>

                <LandingLayout.ImportantNotes>
                  <Td>{t('Important notes')}</Td>
                  <Td>
                    <p>
                      {t(
                        'Lab environments should be launched and created at least 30-45 minutes prior to course start time to ensure that the systems are ready for use and available at course start time.',
                      )}
                    </p>
                    <p>
                      {t(
                        'After the lab environment is in a running state, you should extend the "auto-stop" timer to 4 hours so that it will last for the entire duration of the session plus a little bit longer. If you reset any machine in the lab environment, the auto-stop timer will reset back to the default time and will need to be extended again.',
                      )}
                    </p>
                  </Td>
                </LandingLayout.ImportantNotes>
                <Tr>
                  <Td>{t('Class days')}</Td>
                  <Td>
                    <ClassDaysStepper
                      startTime={start_time}
                      endTime={end_time}
                      timezone={timezone}
                      sessions={sessions}
                    />
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          )}
      </>
    );
  }

  return null;
};

export default observer(LiveClassTab);
